import styled from '@emotion/styled'
import { Primary500, White } from '@mehilainen/mds-customer/colors'
import { ArrowUp, CalendarDay, Times } from '@mehilainen/mds-customer/icons'
import { Badge } from '@mui/material'
import equal from 'fast-deep-equal'
import { transparentize } from 'polished'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Chip from '../../../common/components/Chip/Chip'
import { displayTimerange } from '../../../common/components/DateSelect/TimerangeSelect'
import { MinMaxDiv, RowFlex } from '../../../common/components/Layout/Layout'
import { Text } from '../../../common/components/Typography/Typography'
import { useIsMobile } from '../../../common/hooks/useBreakpoint'
import { maxWidth } from '../../../common/utils/breakpoint'
import debounce from '../../../common/utils/debounce'
import { scale } from '../../../common/utils/scale'
import { capitalizeFirstLetter } from '../../../common/utils/text'
import { defaultSelectedTimeRange } from '../../../state/search/atoms'
import { useModalEvents, useSearchEvents } from '../../../xstate/events'
import {
  useIsUserSelectedDate,
  useSearchFilterOptions,
  useSelectedDate,
} from '../../../xstate/selectors'
import { defaultFilterOptions } from '../../../xstate/types'
import filterIcon from '../components/img/filter.svg'

import { MIN_MAX_WIDTH } from './SearchViewContainers'

const StickyMinMaxDiv = styled(MinMaxDiv)`
  position: fixed;
  top: 0;
  z-index: 1;
  color: ${White};
  background-color: ${transparentize(0.05, Primary500)};
  width: 100%;
  transform: translateY(-100%);
  backdrop-filter: blur(5px);

  @media (min-width: ${maxWidth.body}px) {
    width: ${maxWidth.body}px;
    margin: auto;
  }
`

const Container = styled(RowFlex)`
  padding: ${scale(2)};
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
`

const TextLink = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${scale(2)};
  &:link {
    text-decoration: none;
  }
`

const MobileTextLink = styled(TextLink)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const ArrowUpContainer = styled(RowFlex)`
  align-items: center;
  justify-content: center;
  color: ${Primary500};
  background: ${White};
  border-radius: 50%;
  width: 32px;
  height: 32px;

  svg {
    margin: 0;
  }
`

const StyledArrowUp = styled(ArrowUp)`
  margin-left: 2px; /* The icon is not centered */
`

const ChipContainer = styled(RowFlex)`
  gap: ${scale(1)};
`

const StyledBadge = styled(Badge)`
  .MuiBadge-badge {
    right: 5px;
    top: 5px;
    color: ${Primary500};
    background-color: ${White};
  }
`

const TRANSITION_DURATION = 500 // MS
const SCROLL_DEBOUNCE_DELAY = 100 // MS

interface Props {
  offset: number
  name: string
  locationName?: string
}

const SearchBar: React.FC<React.PropsWithChildren<Props>> = ({ name, locationName, offset }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [hidden, setHidden] = useState(false)
  const { openDateModal, openFilterModal } = useModalEvents()
  const selectedDate = useSelectedDate()
  const userHasSelectedDate = useIsUserSelectedDate()
  const filterOptions = useSearchFilterOptions()
  const { setSearchFilters } = useSearchEvents()
  const timeRangeLabel = `${displayTimerange(filterOptions.timeRange?.start)} - ${displayTimerange(
    filterOptions.timeRange?.end
  )}`

  const changedFilterCount = useMemo(
    () =>
      isMobile
        ? Object.keys(filterOptions).reduce((count, key) => {
            const defaultValue = defaultFilterOptions[key as keyof typeof defaultFilterOptions]
            const currentValue = filterOptions[key as keyof typeof filterOptions]
            return count + (equal(defaultValue, currentValue) ? 0 : 1)
          }, 0)
        : 0,
    [filterOptions, isMobile]
  )
  const calendarLabel = useMemo(
    () =>
      userHasSelectedDate
        ? selectedDate.isToday()
          ? t('common.time.today')
          : selectedDate.isTomorrow()
          ? t('common.time.tomorrow')
          : capitalizeFirstLetter(selectedDate.format('dd D.M.'))
        : t('common.calendar'),
    [userHasSelectedDate, selectedDate, t]
  )

  const onBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    const onScroll = debounce(() => {
      if (!hidden && window.scrollY > offset) {
        setHidden(true)
      } else if (hidden && window.scrollY < offset) {
        setHidden(false)
      }
    }, SCROLL_DEBOUNCE_DELAY)

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll)
    }
    return () => window.removeEventListener('scroll', onScroll)
  }, [hidden, offset])

  return (
    <StickyMinMaxDiv
      size={MIN_MAX_WIDTH}
      style={{
        transform: hidden ? 'translateY(0%)' : undefined,
        transition: `transform ${TRANSITION_DURATION}ms`,
      }}
      aria-hidden="true"
    >
      <Container data-cy="searchBar-container">
        {isMobile ? (
          <MobileTextLink $size={300} as="button" onClick={onBackToTop} tabIndex={-1}>
            <ArrowUp />
          </MobileTextLink>
        ) : (
          <TextLink $size={300} as="button" onClick={onBackToTop} tabIndex={-1}>
            <ArrowUpContainer>
              <StyledArrowUp />
            </ArrowUpContainer>
            {locationName ? `${name} · ${locationName}` : name}
          </TextLink>
        )}
        <ChipContainer>
          <Chip
            icon={<CalendarDay />}
            label={calendarLabel}
            variant="important"
            onClick={openDateModal}
            order="icon-first"
            tabIndex={-1}
          />
          {isMobile ? (
            <StyledBadge badgeContent={changedFilterCount} color="primary" overlap="rectangular">
              <Chip
                icon={filterIcon}
                variant="important"
                label={t('component.searchFilterChips.addFiltersTextShort')}
                onClick={openFilterModal}
                order="icon-first"
              />
            </StyledBadge>
          ) : (
            <Chip
              icon={filterIcon}
              label={t('component.searchFilterChips.addFiltersText')}
              variant="important"
              onClick={openFilterModal}
              order={isMobile ? 'icon-only' : 'icon-first'}
              tabIndex={-1}
            />
          )}
          {!isMobile && filterOptions.timeRange && (
            <Chip
              variant="important"
              label={timeRangeLabel}
              icon={<Times />}
              onClick={() => {
                setSearchFilters({ ...filterOptions, timeRange: defaultSelectedTimeRange })
              }}
            />
          )}
        </ChipContainer>
      </Container>
    </StickyMinMaxDiv>
  )
}

export default SearchBar
